import React, { useCallback, useEffect} from "react";
import { Select, MenuItem, FormControl, SelectChangeEvent } from "@mui/material";
import { useSelector } from "react-redux";
import { getAuth } from "../../../redux/auth/authSlice";
import { useConciergeFilter } from "../../../stateContext/concierge/conciergeFilterContext";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useConciergeData } from "../../../reactQuery/concierge/conciergeQuery";
import { isConciergeUser } from "../../../utils/permissions";

interface ConciergeFilterComponentProps {
  applyDynamicFilters: (filters: { [key: string]: string | null }) => void;
}

const ConciergeFilterComponent: React.FC<ConciergeFilterComponentProps> = ({ applyDynamicFilters }) => {
  const { state, dispatch } = useConciergeFilter();
  const { userInfo } = useSelector(getAuth);
  const { filteredData: concierges } = useConciergeData(userInfo.userDivCode);

  /**
   * Filters the list of concierges to exclude the current user based on their email.
   */
  const dropdownOptions = concierges && (concierges ?? []).filter(({ email }) => email !== userInfo.email);

  const applyFilters = useCallback(
    (value: string) => {
      const filterKeys: { [key: string]: string } = {
        [userInfo.email]: "conciergeEmail",
        [userInfo.userDivCode]: "divisionCode",
        All: "conciergeEmail",
        default: "conciergeEmail",
      };

      const filterKey = filterKeys[value] || filterKeys.default;
      const filterValue = value === "All" ? "!null" : value;
      applyDynamicFilters({ [filterKey]: filterValue });
    },
    [userInfo.email, userInfo.userDivCode, applyDynamicFilters]
  );

  const handleDropdownChange = (event: SelectChangeEvent) => {
    const value = event.target.value as string;
    dispatch({ type: "SET_SELECTED_VALUE", payload: value });
    applyFilters(value);
  };

  /**
   * An array of valid values used to determine which filter values are permitted in the MUI dropdown of Concierge Filter component.
   */
  const validValues = [userInfo.email, userInfo.userDivCode, "All", ...dropdownOptions.map((c) => c.email)];
  const isValidSelectedValue = validValues.includes(state.selectedValue);

  useEffect(() => {
    const selectedValue = state.selectedValue;
    const hasDivisionChanged = state.lastSelectedDivision !== userInfo.userDivCode;
    if (!selectedValue || hasDivisionChanged) {
      const initialValue = isConciergeUser(userInfo.userRoles) ? userInfo.email : userInfo.userDivCode;
      dispatch({ type: "SET_SELECTED_VALUE", payload: initialValue });
      applyFilters(initialValue);
    } else {
      dispatch({ type: "SET_SELECTED_VALUE", payload: selectedValue });
      applyFilters(selectedValue);
    }
    if (hasDivisionChanged) {
      dispatch({ type: "SET_LAST_SELECTED_DIVISION", payload: userInfo.userDivCode });
    }
  }, [
    state.selectedValue,
    userInfo.email,
    userInfo.userDivCode,
    applyFilters,
    dispatch,
    userInfo.userRoles,
    state.lastSelectedDivision,
  ]);

  return (
    <FormControl style={{ width: "265px", marginLeft: "6px" }}>
      <Select
        value={isValidSelectedValue ? state.selectedValue : ""}
        onChange={handleDropdownChange}
        IconComponent={(props) => <KeyboardArrowDownIcon {...props} sx={{ color: "#1C2B36", fontSize: 30 }} />}
        sx={{
          height: "42px",
          backgroundColor: "#FAFBFC",
          borderBottom: "1px solid #1C2B36",
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
        }}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 350,
              overflowY: "auto",
              scrollbarWidth: "none",
            },
          },
        }}
      >
        {isConciergeUser(userInfo.userRoles) && (
          <MenuItem key={userInfo.email} value={userInfo.email}>
            My Overview
          </MenuItem>
        )}
        <MenuItem key={userInfo.userDivCode} value={userInfo.userDivCode}>
          My Division
        </MenuItem>
        <MenuItem key="All" value="All">
          All Concierges
        </MenuItem>
        {dropdownOptions?.map((concierge) => (
          <MenuItem key={concierge.email} value={concierge.email}>
            {concierge.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default ConciergeFilterComponent;
